import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Tooltip } from '@mui/material';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import { useTranslation } from 'react-i18next';

const CHECKBOX_COL_WIDTH = 50;
const FIT_WIDTH_GUTTER = 24;

const availableColumnWidth = (containerWidth, hasCheckbox) =>
    Math.max(0, containerWidth - (hasCheckbox ? CHECKBOX_COL_WIDTH : 0) - FIT_WIDTH_GUTTER);

const isColumnShown = (visibilityModel, field) => visibilityModel?.[field] !== false;

export const computeFittedWidths = (columns, visibilityModel, containerWidth, preferredWidths = {}, hasCheckbox = true) => {
    const available = availableColumnWidth(containerWidth, hasCheckbox);
    const visible = (columns || []).filter((col) => isColumnShown(visibilityModel, col.field));
    if (!visible.length || available <= 0) return null;

    const weights = visible.map((col) => {
        const preferred = preferredWidths?.[col.field];
        if (preferred > 0) return preferred;
        return Math.max(1, col.minWidth || col.width || 80);
    });
    const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
    const widths = {};
    let used = 0;

    visible.forEach((col, index) => {
        const isLast = index === visible.length - 1;
        const remaining = available - used;
        const share = isLast
            ? remaining
            : Math.min(remaining, Math.floor((weights[index] / weightSum) * available));
        widths[col.field] = Math.max(0, share);
        used += widths[col.field];
    });

    return widths;
};

const widthsDiffer = (previous, next, columns, visibilityModel) => (columns || []).some((col) => {
    if (!isColumnShown(visibilityModel, col.field)) return false;
    return Math.abs((previous?.[col.field] || 0) - (next?.[col.field] || 0)) > 1;
});

const readJson = (key) => {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

export function useFitTableColumns({
    storageKey,
    scope = 'default',
    columns,
    visibilityModel,
    widths,
    onPersist,
    apiRef,
    hasCheckbox = true,
    weightMode = 'saved',
}) {
    const containerRef = useRef(null);
    const widthsRef = useRef(widths || {});
    const columnsRef = useRef(columns);
    const visibilityRef = useRef(visibilityModel);
    const onPersistRef = useRef(onPersist);
    const isResizingRef = useRef(false);
    const prevFitRef = useRef(true);
    const fitTurnedOnRef = useRef(false);
    const lastFitNonceRef = useRef(0);

    widthsRef.current = widths || {};
    columnsRef.current = columns;
    visibilityRef.current = visibilityModel;
    onPersistRef.current = onPersist;

    const fitStorageKey = `${storageKey}_fitColumns`;
    const customStorageKey = `${storageKey}_columnWidthsCustom`;

    const [fitEnabled, setFitEnabled] = useState(() => {
        try {
            const stored = localStorage.getItem(fitStorageKey);
            if (stored == null) {
                localStorage.setItem(fitStorageKey, '1');
                return true;
            }
            return stored !== '0';
        } catch {
            return true;
        }
    });
    const [fitNonce, setFitNonce] = useState(0);
    const [customScopes, setCustomScopes] = useState(() => readJson(customStorageKey));
    const [containerWidth, setContainerWidth] = useState(0);

    const widthsCustom = !!customScopes[scope];
    if (fitEnabled && !prevFitRef.current) fitTurnedOnRef.current = true;
    prevFitRef.current = fitEnabled;

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const update = () => {
            if (isResizingRef.current) return;
            setContainerWidth(Math.floor(el.clientWidth));
        };
        update();
        const observer = new ResizeObserver(update);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const api = apiRef?.current;
        if (!api?.subscribeEvent) return undefined;
        const onStart = api.subscribeEvent('columnResizeStart', () => {
            isResizingRef.current = true;
        });
        const onStop = api.subscribeEvent('columnResizeStop', () => {
            isResizingRef.current = false;
        });
        return () => {
            onStart();
            onStop();
        };
    }, [apiRef, containerWidth]);

    const columnSignature = (columns || [])
        .map((col) => `${col.field}:${col.minWidth || col.width || 0}:${visibilityModel?.[col.field] === false ? 0 : 1}`)
        .join('|');

    useLayoutEffect(() => {
        if (!fitEnabled || containerWidth <= 0 || isResizingRef.current) return;
        const nonceChanged = lastFitNonceRef.current !== fitNonce;
        lastFitNonceRef.current = fitNonce;
        const turnedOn = fitTurnedOnRef.current || nonceChanged;
        fitTurnedOnRef.current = false;
        if (widthsCustom && !turnedOn) return;

        const fitted = computeFittedWidths(
            columnsRef.current,
            visibilityRef.current,
            containerWidth,
            weightMode === 'column' ? {} : widthsRef.current,
            hasCheckbox
        );
        if (!fitted) return;
        if (turnedOn) {
            setCustomScopes((prev) => {
                if (!prev[scope]) return prev;
                const next = { ...prev, [scope]: false };
                try { localStorage.setItem(customStorageKey, JSON.stringify(next)); } catch (_) {}
                return next;
            });
        }
        if (!widthsDiffer(widthsRef.current, fitted, columnsRef.current, visibilityRef.current)) return;
        onPersistRef.current({ ...widthsRef.current, ...fitted });
    }, [fitEnabled, containerWidth, columnSignature, widthsCustom, fitNonce, scope, hasCheckbox, customStorageKey, weightMode]);

    const handleColumnWidthChange = (params) => {
        const field = params?.colDef?.field ?? params?.field;
        const width = params?.width;
        isResizingRef.current = false;
        if (!field || !(width >= 40)) return;
        setCustomScopes((prev) => {
            if (prev[scope]) return prev;
            const next = { ...prev, [scope]: true };
            try { localStorage.setItem(customStorageKey, JSON.stringify(next)); } catch (_) {}
            return next;
        });
        onPersistRef.current({ ...widthsRef.current, [field]: width });
    };

    const requestFit = () => {
        if (!fitEnabled) {
            setFitEnabled(true);
            try { localStorage.setItem(fitStorageKey, '1'); } catch (_) {}
        }
        setFitNonce((n) => n + 1);
    };

    const columnsWithWidths = (columns || []).map((col) => {
        const w = widths?.[col.field];
        if (w != null && w > 0) {
            return { ...col, width: w, minWidth: 40, flex: 0 };
        }
        return col;
    });

    return {
        containerRef,
        columnsWithWidths,
        fitEnabled,
        requestFit,
        handleColumnWidthChange,
    };
}

export function FitColumnsButton({ active, onClick, height = 40 }) {
    const { t } = useTranslation();
    return (
        <Tooltip title={t('FitColumnsTooltip')}>
            <Button
                variant={active ? 'contained' : 'outlined'}
                size="small"
                startIcon={<FitScreenIcon />}
                onClick={onClick}
                aria-pressed={active}
                sx={{
                    minHeight: height,
                    height,
                    textTransform: 'none',
                    borderRadius: '10px',
                    borderColor: '#304FFF',
                    color: active ? '#ffffff' : '#304FFF',
                    fontSize: 'var(--xtram-table-view-label, 11px)',
                    fontWeight: 600,
                    background: active ? '#304FFF' : '#f5f6fd',
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                        borderColor: '#304FFF',
                        background: active ? '#2538d6' : '#eaedff',
                        boxShadow: 'none',
                    },
                }}
            >
                {t('FitColumns')}
            </Button>
        </Tooltip>
    );
}
